import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  FormField,
  TextInput,
  Select,
  NumberInput,
  Alert,
  Paper,
  Text,
  Badge,
  MobileListRow,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useListInventoryTransfersQuery,
  useAddUpdateInventoryTransferMutation,
  useConfirmInventoryTransferMutation,
  useDeleteInventoryTransferMutation,
  useListRacksQuery,
  useListWarehousesQuery,
} from '../api/tenantApi';
import { MOCK_PRODUCTS } from '../mock/mockData';
import { useAccess } from '../hooks/useAccess';

type TransferRow = {
  id: string;
  productId: string;
  fromType: 'PURCHASE_POOL' | 'RACK';
  fromRackId: string | null;
  toType: 'RACK' | 'ALLOCATION_POOL';
  toRackId: string | null;
  warehouseId: string;
  quantity: number;
  notes: string | null;
  status: 'DRAFT' | 'CONFIRMED';
  createdAt: string;
  product: { id: string; name: string; unit?: string | null };
  fromRack: { id: string; name: string } | null;
  toRack: { id: string; name: string } | null;
};

type FormState = {
  productId: string;
  fromType: 'PURCHASE_POOL' | 'RACK';
  fromRackId: string;
  toType: 'RACK' | 'ALLOCATION_POOL';
  toRackId: string;
  warehouseId: string;
  quantity: number | '';
  notes: string;
};

const FROM_TYPE_OPTIONS = [
  { value: 'PURCHASE_POOL', label: 'Purchase Pool' },
  { value: 'RACK', label: 'Rack' },
];

const TO_TYPE_OPTIONS = [
  { value: 'RACK', label: 'Rack' },
  { value: 'ALLOCATION_POOL', label: 'Allocation Pool' },
];

function emptyForm(): FormState {
  return {
    productId: '',
    fromType: 'PURCHASE_POOL',
    fromRackId: '',
    toType: 'RACK',
    toRackId: '',
    warehouseId: '',
    quantity: '',
    notes: '',
  };
}

export function InventoryTransferPage() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();
  const [query, setQuery] = useState<ResourceQuery>({ page: 1, pageLimit: 20, search: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TransferRow | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useListInventoryTransfersQuery({
    page: query.page,
    pageLimit: query.pageLimit,
  });
  const { data: racksData } = useListRacksQuery({ page: 1, pageLimit: 200 });
  const { data: warehousesData } = useListWarehousesQuery({ page: 1, pageLimit: 50 });
  const [save, { isLoading: saving }] = useAddUpdateInventoryTransferMutation();
  const [confirm, { isLoading: confirming }] = useConfirmInventoryTransferMutation();
  const [remove] = useDeleteInventoryTransferMutation();

  const list = data?.listInventoryTransfers;

  const productOptions = MOCK_PRODUCTS.map((p) => ({ value: p.id, label: p.name }));
  const rackOptions = (racksData?.listRacks.rows ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));
  const warehouseOptions = (warehousesData?.listWarehouses.rows ?? []).map((w) => ({
    value: w.id,
    label: w.name,
  }));

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setModalOpen(true);
  }

  function openEdit(row: TransferRow) {
    if (row.status === 'CONFIRMED') return;
    setEditing(row);
    setForm({
      productId: row.productId,
      fromType: row.fromType,
      fromRackId: row.fromRackId ?? '',
      toType: row.toType,
      toRackId: row.toRackId ?? '',
      warehouseId: row.warehouseId,
      quantity: row.quantity,
      notes: row.notes ?? '',
    });
    setModalOpen(true);
  }

  async function onSave() {
    await save({
      ...(editing ? { id: editing.id } : {}),
      productId: form.productId,
      fromType: form.fromType,
      fromRackId: form.fromType === 'RACK' ? form.fromRackId || null : null,
      toType: form.toType,
      toRackId: form.toType === 'RACK' ? form.toRackId || null : null,
      warehouseId: form.warehouseId,
      quantity: Number(form.quantity),
      notes: form.notes.trim() || null,
    }).unwrap();
    setModalOpen(false);
    void refetch();
  }

  async function onConfirm() {
    if (!confirmId) return;
    await confirm(confirmId).unwrap();
    setConfirmId(null);
    void refetch();
  }

  function statusColor(status: string) {
    return status === 'CONFIRMED' ? 'green' : 'orange';
  }

  const isFormValid =
    form.productId &&
    form.warehouseId &&
    Number(form.quantity) > 0 &&
    (form.fromType !== 'RACK' || Boolean(form.fromRackId)) &&
    (form.toType !== 'RACK' || Boolean(form.toRackId));

  return (
    <Stack gap="md">
      <PageHeader title={t('inventory.title')} description={t('inventory.transfers')} />

      {isError ? <Alert color="red" title={t('common.connectionError')} /> : null}

      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        emptyTitle={t('inventory.noTransfers')}
        emptyDescription={t('inventory.transferHistory')}
        onRowClick={(row: TransferRow) => openEdit(row)}
        mobileView="list"
        addButton={
          canMutate() ? (
            <Button onClick={openCreate}>{t('inventory.newTransfer')}</Button>
          ) : undefined
        }
        columns={[
          {
            key: 'product',
            header: t('inventory.product'),
            render: (row: TransferRow) => row.product.name,
          },
          {
            key: 'from',
            header: t('inventory.fromType'),
            render: (row: TransferRow) =>
              row.fromType === 'PURCHASE_POOL'
                ? t('inventory.purchasePool')
                : row.fromRack?.name ?? t('common.emptyValue'),
          },
          {
            key: 'to',
            header: t('inventory.toType'),
            render: (row: TransferRow) =>
              row.toType === 'ALLOCATION_POOL'
                ? t('inventory.allocationPool')
                : row.toRack?.name ?? t('common.emptyValue'),
          },
          {
            key: 'quantity',
            header: t('inventory.quantity'),
            render: (row: TransferRow) => `${row.quantity} ${row.product.unit ?? ''}`.trim(),
          },
          {
            key: 'status',
            header: t('inventory.status'),
            render: (row: TransferRow) => (
              <Badge color={statusColor(row.status)}>
                {row.status === 'CONFIRMED' ? t('inventory.confirmed') : t('inventory.draft')}
              </Badge>
            ),
          },
        ]}
        renderCard={(row: TransferRow) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text fw={600}>{row.product.name}</Text>
                <Badge color={statusColor(row.status)}>
                  {row.status === 'CONFIRMED' ? t('inventory.confirmed') : t('inventory.draft')}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {row.fromType === 'PURCHASE_POOL'
                  ? t('inventory.purchasePool')
                  : row.fromRack?.name}{' '}
                → {row.toType === 'ALLOCATION_POOL' ? t('inventory.allocationPool') : row.toRack?.name}
              </Text>
              <Text size="sm">{`${row.quantity} ${row.product.unit ?? ''}`.trim()}</Text>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: TransferRow) => (
          <MobileListRow
            title={row.product.name}
            subtitle={`${row.quantity} ${row.product.unit ?? ''}`.trim()}
            onClick={() => openEdit(row)}
          />
        )}
      />

      {/* Add / Edit drawer */}
      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('inventory.editTransfer') : t('inventory.newTransfer')}
      >
        <Stack gap="md">
          <FormField label={t('inventory.product')} required>
            <Select
              placeholder={t('inventory.product')}
              searchable
              value={form.productId || null}
              onChange={(value) => setForm({ ...form, productId: value ?? '' })}
              data={productOptions}
            />
          </FormField>

          <FormField label={t('inventory.warehouse')} required>
            <Select
              placeholder={t('inventory.warehouse')}
              value={form.warehouseId || null}
              onChange={(value) => setForm({ ...form, warehouseId: value ?? '' })}
              data={warehouseOptions}
            />
          </FormField>

          <FormField label={t('inventory.fromType')} required>
            <Select
              value={form.fromType}
              onChange={(value) =>
                setForm({ ...form, fromType: (value as FormState['fromType']) ?? 'PURCHASE_POOL' })
              }
              data={FROM_TYPE_OPTIONS}
            />
          </FormField>

          {form.fromType === 'RACK' ? (
            <FormField label={t('inventory.fromRack')} required>
              <Select
                placeholder={t('inventory.fromRack')}
                value={form.fromRackId || null}
                onChange={(value) => setForm({ ...form, fromRackId: value ?? '' })}
                data={rackOptions}
              />
            </FormField>
          ) : null}

          <FormField label={t('inventory.toType')} required>
            <Select
              value={form.toType}
              onChange={(value) =>
                setForm({ ...form, toType: (value as FormState['toType']) ?? 'RACK' })
              }
              data={TO_TYPE_OPTIONS}
            />
          </FormField>

          {form.toType === 'RACK' ? (
            <FormField label={t('inventory.toRack')} required>
              <Select
                placeholder={t('inventory.toRack')}
                value={form.toRackId || null}
                onChange={(value) => setForm({ ...form, toRackId: value ?? '' })}
                data={rackOptions}
              />
            </FormField>
          ) : null}

          <FormField label={t('inventory.quantity')} required>
            <NumberInput
              placeholder="0"
              min={1}
              value={form.quantity}
              onChange={(value) => setForm({ ...form, quantity: value as number | '' })}
            />
          </FormField>

          <FormField label={t('inventory.notes')}>
            <TextInput
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.currentTarget.value })}
              placeholder={t('common.optional')}
            />
          </FormField>

          <Group justify="space-between">
            {editing ? (
              <Group gap="xs">
                <Button
                  color="red"
                  variant="light"
                  onClick={async () => {
                    await remove(editing.id);
                    setModalOpen(false);
                    void refetch();
                  }}
                >
                  {t('inventory.deleteTransfer')}
                </Button>
                <Button
                  color="green"
                  variant="light"
                  onClick={() => {
                    setModalOpen(false);
                    setConfirmId(editing.id);
                  }}
                >
                  {t('inventory.confirmTransfer')}
                </Button>
              </Group>
            ) : (
              <span />
            )}
            <Button
              loading={saving}
              disabled={!isFormValid}
              onClick={() => void onSave()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      {/* Confirm transfer dialog */}
      <BottomDrawer
        opened={Boolean(confirmId)}
        onClose={() => setConfirmId(null)}
        title={t('inventory.confirmTransfer')}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('inventory.confirmTransferDesc')}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmId(null)}>
              {t('common.cancel')}
            </Button>
            <Button color="green" loading={confirming} onClick={() => void onConfirm()}>
              {t('inventory.confirmTransfer')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
